import { ButtonWithLoading } from "@/components/shared/buttons/ButtonWithLoading";
import { ErrorModal } from "@/components/shared/modal/error-modal";
import { PageTitle } from "@/components/shared/title/page-title";
import {
	LadleTapTempAlertConfiguration,
	ladlesRepository,
} from "@/repositories/roots/ladlesRepository";
import {
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid,
	Paper,
	Snackbar,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useEffect, useState } from "react";

const EMAIL_TOPIC_NAME = "LADLE_TAP_TEMP_ALERT";

const getErrorMessage = (e: any): string =>
	e?.response?.data?.message ??
	e?.response?.data?.detail ??
	e?.message ??
	String(e);

const DEFAULT_CONFIG: LadleTapTempAlertConfiguration = {
	ladleTapTempAlertConfigurationId: 0,
	alertCode: "TapTemperatureHigh",
	threshold: 0,
	isActive: false,
	sendEmailNotifications: false,
	notificationEmails: null,
	operatorRequired: true,
	commentsEnabled: true,
	updateTimestamp: null,
	updatedBy: null,
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			maxWidth: 600,
			margin: theme.spacing(3),
			padding: theme.spacing(3),
			display: "flex",
			flexDirection: "column",
		},
		header: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: theme.spacing(2),
			marginBottom: theme.spacing(2),
		},
		activeChip: {
			backgroundColor: theme.palette.success.main,
			color: "#fff",
		},
		emailsDisabled: {
			opacity: 0.6,
		},
		divider: {
			margin: theme.spacing(2, 0),
		},
		sectionTitle: {
			color: theme.palette.text.secondary,
			fontWeight: 600,
			marginBottom: theme.spacing(1),
		},
		recipientsHint: {
			display: "block",
			color: theme.palette.text.secondary,
		},
		footer: {
			display: "flex",
			flexDirection: "column",
			alignItems: "stretch",
			gap: theme.spacing(1),
		},
		footerMeta: {
			color: theme.palette.text.secondary,
			alignSelf: "flex-end",
		},
		loadingRoot: {
			maxWidth: 600,
			margin: theme.spacing(3),
			padding: theme.spacing(6),
			display: "flex",
			justifyContent: "center",
		},
	}),
);

type Feedback = { type: "success" | "error"; text: string };

const LadleTapTempAlertPage = () => {
	const classes = useStyles();
	const [config, setConfig] = useState<LadleTapTempAlertConfiguration | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [errorModal, setErrorModal] = useState<string | null>(null);

	useEffect(() => {
		(async () => {
			try {
				const data = await ladlesRepository.getTapTempAlertConfiguration();
				const resolved =
					data && typeof data === "object" ? data : DEFAULT_CONFIG;
				setConfig(resolved);
			} catch (e: any) {
				setConfig(DEFAULT_CONFIG);
				setErrorModal(getErrorMessage(e));
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	const updateField = (
		field: keyof LadleTapTempAlertConfiguration,
		value: any,
	) => {
		setConfig((current) =>
			current ? { ...current, [field]: value } : current,
		);
	};

	const thresholdInvalid = config != null && Number(config.threshold) < 0;
	const isThresholdActive = config != null && Number(config.threshold) > 0;

	const save = async () => {
		if (!config) return;

		if (thresholdInvalid) return;

		setIsSaving(true);
		setFeedback(null);
		try {
			const updated = await ladlesRepository.updateTapTempAlertConfiguration({
				threshold: Number(config.threshold),
				isActive: Number(config.threshold) > 0,
				sendEmailNotifications: config.sendEmailNotifications,
				notificationEmails: config.notificationEmails,
				operatorRequired: config.operatorRequired,
				commentsEnabled: config.commentsEnabled,
			});
			setConfig(updated);
			setFeedback({ type: "success", text: "Configuration saved" });
		} catch (e: any) {
			setErrorModal(getErrorMessage(e));
		} finally {
			setIsSaving(false);
		}
	};

	if (loading || !config) {
		return (
			<>
				<PageTitle title="Ladle Tap Temp Alert" />
				<Paper className={classes.loadingRoot}>
					<CircularProgress />
				</Paper>
				<ErrorModal error={errorModal} onHide={() => setErrorModal(null)} />
			</>
		);
	}

	const alertInactive = !isThresholdActive;

	return (
		<>
			<PageTitle title="Ladle Tap Temp Alert" />
			<Paper className={classes.root}>
				<div className={classes.header}>
					<Typography variant="h5">Ladle Tap Temp Alert</Typography>
					{isThresholdActive ? (
						<Chip label="Active" className={classes.activeChip} />
					) : (
						<Chip label="Inactive" />
					)}
				</div>

				<Typography variant="subtitle2" className={classes.sectionTitle}>
					Alert threshold
				</Typography>
				<Grid container spacing={2} direction="column">
					<Grid item>
						<TextField
							label="Tap temperature threshold"
							type="number"
							value={config.threshold}
							onChange={(event) => updateField("threshold", event.target.value)}
							onFocus={(event) => event.target.select()}
							error={thresholdInvalid}
							helperText={
								thresholdInvalid
									? "Threshold cannot be negative."
									: isThresholdActive
									  ? "Alerts trigger when tap temperature ≥ this value."
									  : "Inactive. Set a value > 0 to activate."
							}
							fullWidth
							variant="outlined"
						/>
					</Grid>
				</Grid>

				<Typography variant="subtitle2" className={classes.sectionTitle}>
					Email notifications
				</Typography>
				<Grid container spacing={2} direction="column">
					<Grid
						item
						className={alertInactive ? classes.emailsDisabled : undefined}
					>
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={config.sendEmailNotifications}
									disabled={alertInactive}
									onChange={(event) =>
										updateField("sendEmailNotifications", event.target.checked)
									}
								/>
							}
							label="Send email notifications"
						/>
						<Typography variant="caption" className={classes.recipientsHint}>
							{`Recipients are managed in the ${EMAIL_TOPIC_NAME} email topic.`}
						</Typography>
					</Grid>
				</Grid>

				<Divider className={classes.divider} />

				<Typography variant="subtitle2" className={classes.sectionTitle}>
					Acknowledge popup
				</Typography>
				<Grid container spacing={2} direction="column">
					<Grid item>
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={config.operatorRequired}
									onChange={(event) =>
										updateField("operatorRequired", event.target.checked)
									}
								/>
							}
							label="Require operator to acknowledge"
						/>
					</Grid>
					<Grid item>
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={config.commentsEnabled}
									onChange={(event) =>
										updateField("commentsEnabled", event.target.checked)
									}
								/>
							}
							label="Enable comments field"
						/>
					</Grid>
				</Grid>

				<Divider className={classes.divider} />

				<div className={classes.footer}>
					<ButtonWithLoading
						color="primary"
						variant="contained"
						onClick={save}
						isLoading={isSaving}
						fullWidth
					>
						Save
					</ButtonWithLoading>
					{config.updatedBy && (
						<Typography variant="caption" className={classes.footerMeta}>
							Last updated by {config.updatedBy}
						</Typography>
					)}
				</div>

				<Snackbar
					open={!!feedback}
					autoHideDuration={3000}
					onClose={() => setFeedback(null)}
				>
					{feedback ? (
						<Alert severity={feedback.type} onClose={() => setFeedback(null)}>
							{feedback.text}
						</Alert>
					) : undefined}
				</Snackbar>

				<ErrorModal error={errorModal} onHide={() => setErrorModal(null)} />
			</Paper>
		</>
	);
};

export default LadleTapTempAlertPage;
